import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  loadAllGames, addGame, updateGame, removeGame,
  parseSgfMetadata, type CollectionGame,
} from '../services/collectionStorage';
import { collectionNavStore } from '../store/collectionNavStore';
import './CollectionPage.css';

type SortCol = 'playerBlack' | 'playerWhite' | 'result' | 'date' | 'moveCount' | 'boardSize' | 'addedAt';
type Tab = 'all' | 'favorites';

// ── Folder drop helpers (FileSystem API) ─────────────────────────────────────

async function readDirEntry(dir: FileSystemDirectoryEntry): Promise<File[]> {
  const files: File[] = [];
  const reader = dir.createReader();
  await new Promise<void>(resolve => {
    const readNext = () => {
      reader.readEntries(async entries => {
        if (entries.length === 0) { resolve(); return; }
        for (const entry of entries) {
          if (entry.isFile) {
            const file = await new Promise<File>(res =>
              (entry as FileSystemFileEntry).file(res)
            );
            files.push(file);
          } else if (entry.isDirectory) {
            files.push(...await readDirEntry(entry as FileSystemDirectoryEntry));
          }
        }
        readNext();
      }, () => resolve());
    };
    readNext();
  });
  return files;
}

async function readDroppedItems(dataTransfer: DataTransfer): Promise<{ files: File[]; collection: string }> {
  const allFiles: File[] = [];
  let collection = 'Unsorted';

  for (const item of Array.from(dataTransfer.items)) {
    if (item.kind !== 'file') continue;
    const entry = item.webkitGetAsEntry();
    if (!entry) continue;

    if (entry.isDirectory) {
      collection = entry.name;
      allFiles.push(...await readDirEntry(entry as FileSystemDirectoryEntry));
    } else if (entry.isFile) {
      const file = await new Promise<File>(res => (entry as FileSystemFileEntry).file(res));
      allFiles.push(file);
    }
  }

  return { files: allFiles, collection };
}

function SortIcon({ col, sortCol, sortDir }: { col: string; sortCol: string; sortDir: 'asc' | 'desc' }) {
  if (col !== sortCol) return <span style={{ color: '#ccc', marginLeft: 4 }}>↕</span>;
  return <span style={{ marginLeft: 4 }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
}

export default function CollectionPage() {
  const [games, setGames] = useState<CollectionGame[]>([]);
  const [filterBlack, setFilterBlack] = useState('');
  const [filterWhite, setFilterWhite] = useState('');
  const [filterSearch, setFilterSearch] = useState('');
  const [filterCollection, setFilterCollection] = useState('');
  const [tab, setTab] = useState<Tab>('all');
  const [sortCol, setSortCol] = useState<SortCol>('addedAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [isDragOver, setIsDragOver] = useState(false);
  const [importProgress, setImportProgress] = useState<{ done: number; total: number } | null>(null);

  const fileInputRef   = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadAllGames().then(setGames).catch(console.error);
  }, []);

  // ── Filtering + sorting ──────────────────────────────────────────────────

  const filteredGames = useMemo(() => {
    let result = tab === 'favorites' ? games.filter(g => g.favorite) : games;

    if (filterCollection) {
      result = result.filter(g => (g.collection ?? 'Unsorted') === filterCollection);
    }
    if (filterBlack) {
      const q = filterBlack.toLowerCase();
      result = result.filter(g => g.playerBlack.toLowerCase().includes(q));
    }
    if (filterWhite) {
      const q = filterWhite.toLowerCase();
      result = result.filter(g => g.playerWhite.toLowerCase().includes(q));
    }
    if (filterSearch) {
      const q = filterSearch.toLowerCase();
      result = result.filter(g =>
        g.playerBlack.toLowerCase().includes(q) ||
        g.playerWhite.toLowerCase().includes(q) ||
        g.filename.toLowerCase().includes(q) ||
        g.result.toLowerCase().includes(q) ||
        g.event?.toLowerCase().includes(q)
      );
    }

    return [...result].sort((a, b) => {
      const va = String(a[sortCol] ?? '');
      const vb = String(b[sortCol] ?? '');
      const cmp = va.localeCompare(vb, undefined, { numeric: true });
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [games, tab, filterBlack, filterWhite, filterSearch, sortCol, sortDir]);

  const favoriteCount = useMemo(() => games.filter(g => g.favorite).length, [games]);

  const collectionNames = useMemo(() => {
    const names = new Set(games.map(g => g.collection ?? 'Unsorted'));
    return Array.from(names).sort();
  }, [games]);

  // ── Sort toggle ──────────────────────────────────────────────────────────

  const toggleSort = (col: SortCol) => {
    if (sortCol === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
  };

  // ── Import ───────────────────────────────────────────────────────────────

  const importFiles = useCallback(async (files: File[], explicitCollection?: string) => {
    const sgfFiles = files.filter(f => f.name.toLowerCase().endsWith('.sgf'));
    if (sgfFiles.length === 0) return;

    // Use explicit name (from folder drop), else detect from webkitRelativePath (folder input), else Unsorted
    const relPath = (sgfFiles[0] as any).webkitRelativePath as string | undefined;
    const collectionName = explicitCollection
      ?? (relPath?.includes('/') ? relPath.split('/')[0] : 'Unsorted');

    setImportProgress({ done: 0, total: sgfFiles.length });
    const CHUNK = 10;

    for (let i = 0; i < sgfFiles.length; i += CHUNK) {
      const chunk = sgfFiles.slice(i, i + CHUNK);
      await Promise.all(chunk.map(async (file) => {
        try {
          const text = await file.text();
          const game = parseSgfMetadata(text, file.name, collectionName);
          await addGame(game);
        } catch (e) {
          console.warn('Failed to import', file.name, e);
        }
      }));
      setImportProgress({ done: Math.min(i + CHUNK, sgfFiles.length), total: sgfFiles.length });
      // Yield to keep UI responsive
      await new Promise(r => setTimeout(r, 0));
    }

    const updated = await loadAllGames();
    setGames(updated);
    setImportProgress(null);
  }, []);

  const handleFileInput = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    await importFiles(files);
    e.target.value = '';
  }, [importFiles]);

  // ── Drag-drop ────────────────────────────────────────────────────────────

  const handleDragOver  = (e: React.DragEvent) => { e.preventDefault(); setIsDragOver(true); };
  const handleDragLeave = (e: React.DragEvent) => { e.preventDefault(); setIsDragOver(false); };
  const handleDrop      = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const { files, collection } = await readDroppedItems(e.dataTransfer);
    await importFiles(files, collection);
  };

  // ── Favorite toggle ──────────────────────────────────────────────────────

  const toggleFavorite = useCallback(async (id: string, current: boolean) => {
    await updateGame(id, { favorite: !current });
    setGames(prev => prev.map(g => g.id === id ? { ...g, favorite: !current } : g));
  }, []);

  // ── Delete ───────────────────────────────────────────────────────────────

  const handleDelete = useCallback(async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!confirm('Remove this game from the collection?')) return;
    await removeGame(id);
    setGames(prev => prev.filter(g => g.id !== id));
  }, []);

  // ── Open game → navigate to board ────────────────────────────────────────

  const handleOpen = useCallback((_game: CollectionGame, index: number) => {
    collectionNavStore.setContext(filteredGames, index);
    window.location.hash = '';
  }, [filteredGames]);

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div
      className={`collection-page${isDragOver ? ' drag-over' : ''}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Header */}
      <div className="col-header">
        <div className="col-header-left">
          <h1 className="col-title">Game Library</h1>
          <span className="col-count">{games.length} games</span>
        </div>
        <div className="col-header-right">
          <button className="col-btn" onClick={() => fileInputRef.current?.click()}>
            + Add Games
          </button>
          <button className="col-btn col-btn-secondary" onClick={() => folderInputRef.current?.click()}>
            + Add Folder
          </button>
          <input ref={fileInputRef}   type="file" accept=".sgf,.SGF" multiple style={{ display: 'none' }} onChange={handleFileInput} />
          <input ref={folderInputRef} type="file" accept=".sgf,.SGF" multiple style={{ display: 'none' }}
            // @ts-ignore — webkitdirectory is not in React types
            webkitdirectory=""
            onChange={handleFileInput}
          />
        </div>
      </div>

      {/* Import progress */}
      {importProgress && (
        <div className="col-progress">
          <div className="col-progress-bar" style={{ width: `${(importProgress.done / importProgress.total) * 100}%` }} />
          <span className="col-progress-text">Importing {importProgress.done} / {importProgress.total}…</span>
        </div>
      )}

      {/* Filter bar */}
      <div className="col-filters">
        <input className="col-filter-input" placeholder="⚫ Black player" value={filterBlack} onChange={e => setFilterBlack(e.target.value)} />
        <input className="col-filter-input" placeholder="⚪ White player" value={filterWhite} onChange={e => setFilterWhite(e.target.value)} />
        <input className="col-filter-input col-filter-wide" placeholder="🔍 Search…" value={filterSearch} onChange={e => setFilterSearch(e.target.value)} />
        {collectionNames.length > 0 && (
          <select className="col-filter-select" value={filterCollection} onChange={e => setFilterCollection(e.target.value)}>
            <option value="">All collections</option>
            {collectionNames.map(name => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>
        )}
        {(filterBlack || filterWhite || filterSearch || filterCollection) && (
          <button className="col-clear-btn" onClick={() => { setFilterBlack(''); setFilterWhite(''); setFilterSearch(''); setFilterCollection(''); }}>
            Clear
          </button>
        )}
        <span className="col-results">{filteredGames.length} results</span>
      </div>

      {/* Tabs */}
      <div className="col-tabs">
        <button className={`col-tab${tab === 'all' ? ' active' : ''}`} onClick={() => setTab('all')}>
          All <span className="col-tab-count">{games.length}</span>
        </button>
        <button className={`col-tab${tab === 'favorites' ? ' active' : ''}`} onClick={() => setTab('favorites')}>
          ★ Favorites <span className="col-tab-count">{favoriteCount}</span>
        </button>
      </div>

      {/* Table */}
      <div className="col-table-wrap">
        {filteredGames.length === 0 ? (
          <div className="col-empty">
            {games.length === 0
              ? 'No games yet. Click "Add Games" or drop SGF files here.'
              : 'No games match the current filter.'}
          </div>
        ) : (
          <table className="col-table">
            <thead>
              <tr>
                <th style={{ width: 40 }}>★</th>
                <th className="sortable" onClick={() => toggleSort('playerBlack')}>
                  Black <SortIcon col="playerBlack" sortCol={sortCol} sortDir={sortDir} />
                </th>
                <th className="sortable" onClick={() => toggleSort('playerWhite')}>
                  White <SortIcon col="playerWhite" sortCol={sortCol} sortDir={sortDir} />
                </th>
                <th className="sortable" style={{ width: 80 }} onClick={() => toggleSort('result')}>
                  Result <SortIcon col="result" sortCol={sortCol} sortDir={sortDir} />
                </th>
                <th className="sortable" style={{ width: 100 }} onClick={() => toggleSort('date')}>
                  Date <SortIcon col="date" sortCol={sortCol} sortDir={sortDir} />
                </th>
                <th className="sortable" style={{ width: 60 }} onClick={() => toggleSort('moveCount')}>
                  Moves <SortIcon col="moveCount" sortCol={sortCol} sortDir={sortDir} />
                </th>
                <th className="sortable" style={{ width: 55 }} onClick={() => toggleSort('boardSize')}>
                  Size <SortIcon col="boardSize" sortCol={sortCol} sortDir={sortDir} />
                </th>
                <th className="sortable" onClick={() => toggleSort('collection' as SortCol)}>
                  Collection <SortIcon col="collection" sortCol={sortCol} sortDir={sortDir} />
                </th>
                <th style={{ width: 90 }}></th>
              </tr>
            </thead>
            <tbody>
              {filteredGames.map((g, idx) => (
                <tr key={g.id} onDoubleClick={() => handleOpen(g, idx)}>
                  <td className="col-star-cell">
                    <button
                      className={`col-star${g.favorite ? ' on' : ''}`}
                      onClick={() => toggleFavorite(g.id, g.favorite)}
                      title={g.favorite ? 'Remove from favorites' : 'Add to favorites'}
                    >
                      {g.favorite ? '★' : '☆'}
                    </button>
                  </td>
                  <td className="col-player">{g.playerBlack}</td>
                  <td className="col-player">{g.playerWhite}</td>
                  <td className="col-result">{g.result}</td>
                  <td className="col-date">{g.date}</td>
                  <td className="col-num">{g.moveCount}</td>
                  <td className="col-num">{g.boardSize}</td>
                  <td className="col-collection" onClick={e => { e.stopPropagation(); setFilterCollection(g.collection ?? 'Unsorted'); }} title="Filter by this collection">
                    {g.collection ?? 'Unsorted'}
                  </td>
                  <td className="col-actions">
                    <button className="col-open-btn" onClick={() => handleOpen(g, idx)}>Open</button>
                    <button className="col-del-btn" onClick={e => handleDelete(g.id, e)} title="Remove">×</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {isDragOver && (
        <div className="col-drop-overlay">
          <div className="col-drop-msg">Drop SGF files to import</div>
        </div>
      )}
    </div>
  );
}
